import Head from 'next/head';
import React, { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/router';
import { CartAbandonmentReasonEnum, CouponStatusEnum, UserTypeEnum } from 'enums';
import CheckoutLayout from 'layouts/CheckoutLayout';
import Page from 'components/Checkout/Page';
import ProfileService from 'services/ProfileService';
import { isUserFromJournal } from 'utils';
import CartAbandonmentModal from 'components/Checkout/CartAbandonmentModal';
import PaymentIssueModal from 'components/Checkout/PaymentIssueModal';
import NotReadyModal from 'components/Checkout/NotReadyModal';
import DiscountOfferModal from 'components/Checkout/DiscountOfferModal';
import ThanksModal from 'components/Checkout/ThanksModal';
import NotificationService from 'services/NotificationService';
import CartAbandonmentService from 'services/CartAbandonmentService';
import CouponService from 'services/CouponService';
import CartOfferTrial from 'components/Checkout/CartOfferTrial';
import CartActivateFreeTrial from 'components/Checkout/CartActivateFreeTrial';
import EmbargoedCountryModal from 'components/Checkout/EmbargoedCountryModal';
import PageChina from 'components/Checkout/PageChina';
import PageJapan from 'components/Checkout/PageJapan';
import CartAbandonmentModalChina from 'components/Checkout/CartAbandonmentModalChina';
import { CheckoutProviderWrapper } from 'contexts/CheckoutContext';
import { withAuth } from 'features/auth/withAuth';
import { useAuthUser } from 'features/auth/AuthUserContext';
import { withJournalRedirect } from 'hocs/withJournalRedirect';

function Checkout() {
  const router = useRouter();
  const { user, profile } = useAuthUser();
  const [coupon, setCoupon] = useState();
  const [cartAbandonmentData, setCartAbandonmentData] = useState({
    reason: null,
    detail: null,
    description: null,
  });
  const [cartAbandonmentModalOpen, setCartAbandonmentModalOpen] = useState(false);
  const [cartOfferTrialModalOpen, setCartOfferTrialModalOpen] = useState(false);
  const [paymentIssueModalOpen, setPaymentIssueModalOpen] = useState(false);
  const [cartActivateFreeTrialModalOpen, setCartActivateFreeTrialModalOpen] = useState(false);
  const [notReadyModalOpen, setNotReadyModalOpen] = useState(false);
  const [discountOfferModalOpen, setDiscountOfferModalOpen] = useState(false);
  const [thanksModalOpen, setThanksModalOpen] = useState(false);
  const [embargoedCountryModalOpen, setEmbargoedCountryModalOpen] = useState(false);
  const shouldHandleCartAbandonment = useRef(false);
  const notificationService = new NotificationService();
  const cartAbandonmentService = new CartAbandonmentService();
  const couponService = new CouponService();
  const [userLastInvoice, setUserLastInvoice] = useState([]);
  const userIsStarter = user.tp_user === UserTypeEnum.TRIAL;
  const profileService = new ProfileService();
  const userFromJournal = isUserFromJournal(profile);
  const isUserEligibleForTrial = useRef(false);
  const getCoupon = router.asPath;
  const seasonBreakCoupon = getCoupon.includes('promotion=season_break');
  const [isChina, setIsChina] = useState(null);
  const [isJapan, setIsJapan] = useState(null);

  useEffect(() => {
    router.push('/pricing');
  }, [router]);

  /*useEffect(() => {
    async function fetchCheckoutData() {
      try {
        setIsChina(user?.country_code === 'CN');
        setIsJapan(user?.country_code === 'JP');
      } catch (e) {
        console.error(e);
      }
    }

    fetchCheckoutData();
  }, []);

  useEffect(() => {
    isUserEligibleForTrial.current = userIsStarter && !userFromJournal && !userLastInvoice;
  }, [userIsStarter, userFromJournal, userLastInvoice]);*/

  async function userHasInvoice() {
    try {
      const response = await profileService.getLastInvoice();

      setUserLastInvoice(response.data);
    } catch (e) {
      console.error(e);
    }
  }

  function handleAllPlansClick() {
    if (shouldHandleCartAbandonment.current) {
      shouldHandleCartAbandonment.current = false;

      if (userIsStarter && !seasonBreakCoupon) {
        setCartAbandonmentModalOpen(true);
      } else {
        router.push('/my-account/billing/available-plans');
      }
    } else {
      router.push('/my-account/billing/available-plans');
    }
  }

  async function createCartAbandonment(data) {
    try {
      await cartAbandonmentService.create({
        reason: data.reason,
        detail: data.detail || null,
        description: data.description || null,
      });
    } catch (e) {
      console.error(e);
    }
  }

  async function handleCartAbandonmentModalActionClick(data) {
    setCartAbandonmentModalOpen(false);

    if (data.reason === CartAbandonmentReasonEnum.PAYMENT_ISSUE) {
      setCartAbandonmentData({ ...cartAbandonmentData, ...data });

      setPaymentIssueModalOpen(true);
    } else if (data.reason === CartAbandonmentReasonEnum.NOT_READY) {
      setCartAbandonmentData({ ...cartAbandonmentData, ...data });

      setPaymentIssueModalOpen(true);
    } else {
      await createCartAbandonment({ ...cartAbandonmentData, ...data });

      if (data.reason === CartAbandonmentReasonEnum.TOO_EXPENSIVE) {
        if (isUserEligibleForTrial.current) {
          setCartActivateFreeTrialModalOpen(true);
        } else {
          setDiscountOfferModalOpen(true);
        }
      } else if (data.reason === CartAbandonmentReasonEnum.WRONG_PLAN) {
        router.push('/pricing');
      } else {
        setPaymentIssueModalOpen(true);
      }
    }
  }

  async function handlePaymentIssueModalActionClick(data) {
    await createCartAbandonment({ ...cartAbandonmentData, ...data });

    setPaymentIssueModalOpen(false);
    setNotReadyModalOpen(true);
  }

  async function handleNotReadyModalActionClick(data) {
    await createCartAbandonment({ ...cartAbandonmentData, ...data });

    setNotReadyModalOpen(false);
    setThanksModalOpen(true);
  }

  async function handleDiscountOfferModalActionClick() {
    try {
      const { data: coupons } = await couponService.findAll();
      const cartAbandonmentCoupon = coupons.find(
        (coupon) =>
          coupon.context === 'cart_abandonment' &&
          coupon.percent === 10 &&
          coupon.status === CouponStatusEnum.AVAILABLE,
      );

      if (!cartAbandonmentCoupon) {
        throw new Error('Failed to get abandonment coupon.');
      }

      setDiscountOfferModalOpen(false);
      setCoupon(cartAbandonmentCoupon);
    } catch (e) {
      console.error(e);
    }
  }

  function resolvePageComponent() {
    if (user?.country_code === 'CN' || isChina) {
      return (
        <PageChina
          coupon={coupon}
          onAllPlansButtonClick={handleAllPlansClick}
          setEmbargoedCountryModalOpen={setEmbargoedCountryModalOpen}
        />
      );
    }

    if (user?.country_code === 'JP' || isJapan) {
      return (
        <PageJapan
          coupon={coupon}
          onAllPlansButtonClick={handleAllPlansClick}
          setEmbargoedCountryModalOpen={setEmbargoedCountryModalOpen}
        />
      );
    }

    return (
      <CheckoutProviderWrapper>
        <Page
          coupon={coupon}
          onAllPlansButtonClick={handleAllPlansClick}
          setEmbargoedCountryModalOpen={setEmbargoedCountryModalOpen}
        />
      </CheckoutProviderWrapper>
    );
  }

  /*useEffect(() => {
    ouibounce(undefined, {
      cookieExpire: 0,
      sensitivity: 10,
      callback: () => {
        if (shouldHandleCartAbandonment.current) {
          shouldHandleCartAbandonment.current = false;
          if (userIsStarter && !seasonBreakCoupon) {
            setCartAbandonmentModalOpen(true);
          }
        }
      },
    });
  }, []);

  useEffect(() => {
    async function fetchCardAbandonmentNotification() {
      try {
        const { data: cartAbandonmentNotification } =
          await notificationService.hasVisualized('CART_ABANDONMENT');

        if (!cartAbandonmentNotification.visualized) {
          shouldHandleCartAbandonment.current = true;
        } else {
          const visualizedDate = moment.utc(cartAbandonmentNotification.date);
          const daysFromLastVisualization = moment.utc().diff(visualizedDate, 'days');

          if (daysFromLastVisualization >= 7) {
            shouldHandleCartAbandonment.current = true;
          }
        }
      } catch (e) {
        console.error(e);
      }
    }

    fetchCardAbandonmentNotification();
    userHasInvoice();
  }, [user]);

  useEffect(() => {
    const { couponCode, promotion } = router.query;

    async function resolveCoupon() {
      try {
        const { data: coupons } = await couponService.findAll();
        const coupon = coupons.find((coupon) => {
          if (couponCode) {
            return coupon.code === couponCode;
          }

          return coupon.context === promotion;
        });

        if (coupon) {
          setCoupon(coupon);
        }
      } catch (e) {
        console.error(e);
      }
    }

    if (couponCode || promotion) {
      resolveCoupon();
    }
  }, [user, router.query]);*/

  return (
    <CheckoutLayout>
      <Head>
        <title>Checkout - Mind the Graph</title>
      </Head>

      <CartOfferTrial
        open={cartOfferTrialModalOpen}
        onClose={() => {
          setCartOfferTrialModalOpen(false);
          setCartAbandonmentModalOpen(true);
        }}
        clickBtnWhite={() => {
          setCartOfferTrialModalOpen(false);
          setCartAbandonmentModalOpen(true);
        }}
        clickBtnPurple={() => router.push('/my-account/billing/trial-checkout')}
      />

      {user.country_code === 'CN' || isChina ? (
        <CartAbandonmentModalChina
          open={cartAbandonmentModalOpen}
          onClose={() => setCartAbandonmentModalOpen(false)}
          onActionClick={handleCartAbandonmentModalActionClick}
        />
      ) : (
        <CartAbandonmentModal
          open={cartAbandonmentModalOpen}
          onClose={() => setCartAbandonmentModalOpen(false)}
          onActionClick={handleCartAbandonmentModalActionClick}
        />
      )}

      <PaymentIssueModal
        open={paymentIssueModalOpen}
        onClose={() => setPaymentIssueModalOpen(false)}
        onActionClick={handlePaymentIssueModalActionClick}
      />

      <NotReadyModal
        open={notReadyModalOpen}
        onClose={() => setNotReadyModalOpen(false)}
        onActionClick={handleNotReadyModalActionClick}
      />

      <CartActivateFreeTrial
        open={cartActivateFreeTrialModalOpen}
        onClose={() => {
          setCartActivateFreeTrialModalOpen(false);
          setThanksModalOpen(true);
        }}
      />

      <DiscountOfferModal
        open={discountOfferModalOpen}
        onClose={() => setDiscountOfferModalOpen(false)}
        onActionClick={handleDiscountOfferModalActionClick}
      />

      <ThanksModal open={thanksModalOpen} onClose={() => setThanksModalOpen(false)} />

      <EmbargoedCountryModal
        open={embargoedCountryModalOpen}
        onClose={() => setEmbargoedCountryModalOpen(false)}
      />

      {resolvePageComponent()}
    </CheckoutLayout>
  );
}

export default withAuth(withJournalRedirect(Checkout));
